import { useSelector } from "react-redux";
import DataTableBase from "./DataTableBase";
import React, { useState } from "react";
import { store } from "../../store";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import {
  Person,
  AdminPanelSettings,
  Business,
  Edit,
  Delete,
  Email,
  Store,
} from "@mui/icons-material";
import { CONFIG } from "../../config";

export default function UsersTable() {
  const users = useSelector((state) => state.users);
  const brands = useSelector((state) => state.brands);
  const establishments = useSelector((state) => state.establishments);
  const userDB = useSelector((state) => state.userDB);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", admin: false });

  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);

  // Get brands where user is owner
  const getUserOwnedBrands = (userId) => {
    if (!brands) return [];
    return brands.filter((brand) => brand.owner_id === userId);
  };

  // Get brands where user is collaborator (not owner)
  const getUserCollaboratorBrands = (userId) => {
    if (!brands) return [];
    return brands.filter(
      (brand) =>
        brand.owner_id !== userId &&
        brand.collaborators &&
        brand.collaborators.includes(userId)
    );
  };

  // Get all brands where user has access (owner or collaborator)
  const getUserBrands = (userId) => {
    if (!brands) return [];
    return brands.filter(
      (brand) =>
        brand.owner_id === userId ||
        (brand.collaborators && brand.collaborators.includes(userId))
    );
  };

  // Get establishments for user (via their brands)
  const getUserEstablishments = (userId) => {
    if (!establishments || !brands) return [];
    const userBrandIds = getUserBrands(userId).map((b) => b.id);
    return establishments.filter((est) => userBrandIds.includes(est.brand_id));
  };

  // Handle edit
  const handleEditClick = (user, e) => {
    e.stopPropagation();
    setEditingUser(user);
    setEditForm({
      name: user.name || "",
      admin: user.admin || false,
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${CONFIG.API_URL}/users/${editingUser.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editForm),
        }
      );

      if (response.ok) {
        // Refresh users list
        const usersResponse = await fetch(`${CONFIG.API_URL}/users/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          store.dispatch({ type: "users/update", payload: usersData });
        }

        store.dispatch({
          type: "snackbar/update",
          payload: {
            open: true,
            message: "Utilisateur modifié avec succès",
            severity: "success",
          },
        });
        setEditModalOpen(false);
      } else {
        const error = await response.json();
        store.dispatch({
          type: "snackbar/update",
          payload: {
            open: true,
            message: error.detail || "Erreur lors de la modification",
            severity: "error",
          },
        });
      }
    } catch (err) {
      store.dispatch({
        type: "snackbar/update",
        payload: {
          open: true,
          message: "Erreur réseau: " + err.message,
          severity: "error",
        },
      });
    }
  };

  // Handle delete
  const handleDeleteClick = (user, e) => {
    e.stopPropagation();
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${CONFIG.API_URL}/users/${userToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        // Refresh users list
        const usersResponse = await fetch(`${CONFIG.API_URL}/users/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          store.dispatch({ type: "users/update", payload: usersData });
        }

        store.dispatch({
          type: "snackbar/update",
          payload: {
            open: true,
            message: "Utilisateur supprimé avec succès",
            severity: "success",
          },
        });
        setDeleteModalOpen(false);
      } else {
        const error = await response.json();
        store.dispatch({
          type: "snackbar/update",
          payload: {
            open: true,
            message: error.detail || "Erreur lors de la suppression",
            severity: "error",
          },
        });
      }
    } catch (err) {
      store.dispatch({
        type: "snackbar/update",
        payload: {
          open: true,
          message: "Erreur réseau: " + err.message,
          severity: "error",
        },
      });
    }
  };

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        display: true,
        filter: false,
        sort: true,
      },
    },
    {
      name: "email",
      label: "Email",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Email sx={{ color: "#26A69A", fontSize: 20 }} />
              <Typography sx={{ color: "#ffffff", fontWeight: 500 }}>
                {value}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "name",
      label: "Nom",
      options: {
        display: false,
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Person sx={{ color: "#c4a574", fontSize: 20 }} />
              <Typography sx={{ color: "#ffffff", fontWeight: 500 }}>
                {value || "-"}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "admin",
      label: "Rôle",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return value ? (
            <Chip
              icon={<AdminPanelSettings sx={{ fontSize: 16 }} />}
              label="Admin"
              size="small"
              sx={{
                backgroundColor: "rgba(239, 68, 68, 0.15)",
                color: "#ef4444",
                fontWeight: 600,
                "& .MuiChip-icon": { color: "#ef4444" },
              }}
            />
          ) : (
            <Chip
              icon={<Person sx={{ fontSize: 16 }} />}
              label="Utilisateur"
              size="small"
              sx={{
                backgroundColor: "rgba(38, 166, 154, 0.15)",
                color: "#26A69A",
                fontWeight: 500,
                "& .MuiChip-icon": { color: "#26A69A" },
              }}
            />
          );
        },
      },
    },
    {
      name: "id",
      label: "Enseignes",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          const ownedBrands = getUserOwnedBrands(value);
          if (ownedBrands.length === 0) {
            return (
              <Typography sx={{ color: "#6b7280", fontStyle: "italic" }}>
                Aucune
              </Typography>
            );
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {ownedBrands.slice(0, 2).map((brand) => (
                <Chip
                  key={brand.id}
                  icon={<Business sx={{ fontSize: 14 }} />}
                  label={brand.name}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(120, 168, 130, 0.15)",
                    color: "#78a882",
                    fontSize: "0.75rem",
                    "& .MuiChip-icon": { color: "#78a882" },
                  }}
                />
              ))}
              {ownedBrands.length > 2 && (
                <Chip
                  label={`+${ownedBrands.length - 2}`}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(107, 114, 128, 0.15)",
                    color: "#9ca3af",
                    fontSize: "0.75rem",
                  }}
                />
              )}
            </Box>
          );
        },
      },
    },
    {
      name: "id",
      label: "Collaborateur de",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          const collabBrands = getUserCollaboratorBrands(value);
          if (collabBrands.length === 0) {
            return (
              <Typography sx={{ color: "#6b7280", fontStyle: "italic" }}>
                Aucune
              </Typography>
            );
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {collabBrands.slice(0, 2).map((brand) => (
                <Chip
                  key={brand.id}
                  icon={<Business sx={{ fontSize: 14 }} />}
                  label={brand.name}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(196, 165, 116, 0.15)",
                    color: "#c4a574",
                    fontSize: "0.75rem",
                    "& .MuiChip-icon": { color: "#c4a574" },
                  }}
                />
              ))}
              {collabBrands.length > 2 && (
                <Chip
                  label={`+${collabBrands.length - 2}`}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(107, 114, 128, 0.15)",
                    color: "#9ca3af",
                    fontSize: "0.75rem",
                  }}
                />
              )}
            </Box>
          );
        },
      },
    },
    {
      name: "id",
      label: "Établissements",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          const userEstablishments = getUserEstablishments(value);
          if (userEstablishments.length === 0) {
            return (
              <Typography sx={{ color: "#6b7280", fontStyle: "italic" }}>
                Aucun
              </Typography>
            );
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {userEstablishments.slice(0, 2).map((est) => (
                <Chip
                  key={est.id}
                  icon={<Store sx={{ fontSize: 14 }} />}
                  label={est.name}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(107, 157, 173, 0.15)",
                    color: "#6b9dad",
                    fontSize: "0.75rem",
                    "& .MuiChip-icon": { color: "#6b9dad" },
                  }}
                />
              ))}
              {userEstablishments.length > 2 && (
                <Chip
                  label={`+${userEstablishments.length - 2}`}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(107, 114, 128, 0.15)",
                    color: "#9ca3af",
                    fontSize: "0.75rem",
                  }}
                />
              )}
            </Box>
          );
        },
      },
    },
    {
      name: "created_at",
      label: "Inscription",
      options: {
        display: false,
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          if (!value) return "-";
          const date = new Date(value);
          return (
            <Typography sx={{ color: "#9ca3af", fontSize: "0.875rem" }}>
              {date.toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </Typography>
          );
        },
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          // Use ID to find user (safer when table is sorted/filtered)
          const user = users.find((u) => u.id === value);
          const isCurrentUser = userDB && userDB.id === value;

          if (!user) return null;

          return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Modifier">
                <IconButton
                  size="small"
                  onClick={(e) => handleEditClick(user, e)}
                  sx={{
                    color: "#3b82f6",
                    "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.1)" },
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  isCurrentUser
                    ? "Impossible de supprimer votre propre compte"
                    : "Supprimer"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(user, e)}
                    disabled={isCurrentUser}
                    sx={{
                      color: isCurrentUser ? "#4b5563" : "#ef4444",
                      "&:hover": {
                        backgroundColor: isCurrentUser
                          ? "transparent"
                          : "rgba(239, 68, 68, 0.1)",
                      },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  const tableActions = [
    {
      name: `Supprimer ${selectedUsers.length} utilisateur(s)`,
      icon: "Delete",
      action: () => {
        store.dispatch({
          type: "deleteDialog/update",
          payload: {
            open: true,
            type: "users",
            records: selectedUsers,
          },
        });
      },
    },
  ];

  if (!users) {
    return <div>Chargement...</div>;
  }

  return (
    <>
      <DataTableBase
        columns={columns}
        data={users}
        title="Utilisateurs"
        id="users-list"
        name="users-list"
        pageSize={10}
        selectedItems={selectedUsers}
        setSelectedItems={setSelectedUsers}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        selectableRows={userDB && userDB.admin ? "multiple" : "none"}
        actions={tableActions}
        sortOrder="id"
        onRowClick={(rowData) => {
          const userId = rowData[0];
          const user = users.find((u) => u.id === userId);
          if (user) {
            setDetailUser(user);
            setDetailModalOpen(true);
          }
        }}
      />

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1a1f1a",
            border: "1px solid #3d4a37",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#ffffff",
            borderBottom: "1px solid #3d4a37",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Edit sx={{ color: "#3b82f6" }} />
          Modifier l'utilisateur
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography sx={{ color: "#9ca3af", fontSize: "0.875rem" }}>
              Email: {editingUser?.email}
            </Typography>
            <TextField
              label="Nom"
              value={editForm.name}
              onChange={(e) =>
                setEditForm({ ...editForm, name: e.target.value })
              }
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#ffffff",
                  "& fieldset": { borderColor: "#3d4a37" },
                  "&:hover fieldset": { borderColor: "#26A69A" },
                  "&.Mui-focused fieldset": { borderColor: "#26A69A" },
                },
                "& .MuiInputLabel-root": { color: "#9ca3af" },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.admin}
                  onChange={(e) =>
                    setEditForm({ ...editForm, admin: e.target.checked })
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#ef4444",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#ef4444",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AdminPanelSettings
                    sx={{ color: editForm.admin ? "#ef4444" : "#6b7280" }}
                  />
                  <Typography sx={{ color: "#ffffff" }}>
                    Administrateur
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #3d4a37", p: 2 }}>
          <Button
            onClick={() => setEditModalOpen(false)}
            sx={{ color: "#9ca3af" }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            sx={{
              backgroundColor: "#26A69A",
              "&:hover": { backgroundColor: "#00897B" },
            }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1f1a",
            border: "1px solid #3d4a37",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Delete sx={{ color: "#ef4444" }} />
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#9ca3af" }}>
            Êtes-vous sûr de vouloir supprimer l'utilisateur{" "}
            <strong style={{ color: "#ffffff" }}>{userToDelete?.email}</strong>{" "}
            ?
          </Typography>
          <Typography
            sx={{ color: "#f97316", mt: 1, fontSize: "0.875rem" }}
          >
            ⚠️ Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #3d4a37", p: 2 }}>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            sx={{ color: "#9ca3af" }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              backgroundColor: "#ef4444",
              "&:hover": { backgroundColor: "#dc2626" },
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1a1f1a",
            border: "1px solid #3d4a37",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#ffffff",
            borderBottom: "1px solid #3d4a37",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Person sx={{ color: "#26A69A" }} />
          Détail de l'utilisateur
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {detailUser && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* User Info */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, backgroundColor: "rgba(38, 166, 154, 0.05)", borderRadius: 1 }}>
                <Email sx={{ color: "#26A69A", fontSize: 24 }} />
                <Box>
                  <Typography sx={{ color: "#9ca3af", fontSize: "0.75rem" }}>Email</Typography>
                  <Typography sx={{ color: "#ffffff", fontWeight: 600 }}>{detailUser.email}</Typography>
                </Box>
                <Box sx={{ ml: "auto" }}>
                  {detailUser.admin ? (
                    <Chip
                      icon={<AdminPanelSettings sx={{ fontSize: 16 }} />}
                      label="Admin"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(239, 68, 68, 0.15)",
                        color: "#ef4444",
                        fontWeight: 600,
                        "& .MuiChip-icon": { color: "#ef4444" },
                      }}
                    />
                  ) : (
                    <Chip
                      icon={<Person sx={{ fontSize: 16 }} />}
                      label="Utilisateur"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(38, 166, 154, 0.15)",
                        color: "#26A69A",
                        fontWeight: 500,
                        "& .MuiChip-icon": { color: "#26A69A" },
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Owned Brands */}
              <Box>
                <Typography sx={{ color: "#78a882", fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <Business sx={{ fontSize: 20 }} />
                  Enseignes (Propriétaire) — {getUserOwnedBrands(detailUser.id).length}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, pl: 3.5 }}>
                  {getUserOwnedBrands(detailUser.id).length === 0 ? (
                    <Typography sx={{ color: "#6b7280", fontStyle: "italic" }}>Aucune enseigne</Typography>
                  ) : (
                    getUserOwnedBrands(detailUser.id).map((brand) => (
                      <Chip
                        key={brand.id}
                        icon={<Business sx={{ fontSize: 14 }} />}
                        label={brand.name}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(120, 168, 130, 0.15)",
                          color: "#78a882",
                          "& .MuiChip-icon": { color: "#78a882" },
                        }}
                      />
                    ))
                  )}
                </Box>
              </Box>

              {/* Collaborator Brands */}
              <Box>
                <Typography sx={{ color: "#c4a574", fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <Business sx={{ fontSize: 20 }} />
                  Collaborateur de — {getUserCollaboratorBrands(detailUser.id).length}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, pl: 3.5 }}>
                  {getUserCollaboratorBrands(detailUser.id).length === 0 ? (
                    <Typography sx={{ color: "#6b7280", fontStyle: "italic" }}>Aucune collaboration</Typography>
                  ) : (
                    getUserCollaboratorBrands(detailUser.id).map((brand) => (
                      <Chip
                        key={brand.id}
                        icon={<Business sx={{ fontSize: 14 }} />}
                        label={brand.name}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(196, 165, 116, 0.15)",
                          color: "#c4a574",
                          "& .MuiChip-icon": { color: "#c4a574" },
                        }}
                      />
                    ))
                  )}
                </Box>
              </Box>

              {/* Establishments */}
              <Box>
                <Typography sx={{ color: "#6b9dad", fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <Store sx={{ fontSize: 20 }} />
                  Établissements accessibles — {getUserEstablishments(detailUser.id).length}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, pl: 3.5 }}>
                  {getUserEstablishments(detailUser.id).length === 0 ? (
                    <Typography sx={{ color: "#6b7280", fontStyle: "italic" }}>Aucun établissement</Typography>
                  ) : (
                    getUserEstablishments(detailUser.id).map((est) => (
                      <Chip
                        key={est.id}
                        icon={<Store sx={{ fontSize: 14 }} />}
                        label={est.name}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(107, 157, 173, 0.15)",
                          color: "#6b9dad",
                          "& .MuiChip-icon": { color: "#6b9dad" },
                        }}
                      />
                    ))
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #3d4a37", p: 2 }}>
          <Button
            onClick={() => setDetailModalOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: "#26A69A",
              "&:hover": { backgroundColor: "#00897B" },
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
